import React, { useContext, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AuthContext } from "../../context/AuthContext";
import Navbar from "./Navbar";
import { fetchWithAuth } from "../../utils/api";
import { swalError, swalSuccess, swalWarning } from "../../utils/swalAlert";
import Modal from "./Modal";
import PhoneInput from "react-phone-input-2";
import Loader from "./Loader";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const UserProfile = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  let { user, setUser } = useContext(AuthContext);

  const [editUser, setEditUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    receiveWhatsAppMessages: false,
    receiveEmailMessages: false,
  });
  const [initialEditUser, setInitialEditUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    receiveWhatsAppMessages: false,
    receiveEmailMessages: false,
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditChanged, setIsEditChanged] = useState(false);
  const [errors, setErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Validate the edit form
  const validateEditForm = () => {
    const newErrors = {};
    if (!editUser.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!editUser.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!editUser.email?.trim()) newErrors.email = "Email is required";
    if (editUser.email && !/\S+@\S+\.\S+/.test(editUser.email))
      newErrors.email = "Email is invalid";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // helper to strip plus, dashes, spaces and parentheses
  const cleanPhone = (val) =>
    val ? String(val).replace(/[+\-\s()]/g, "") : "";

  const handleEditModalOpen = (user) => {
    setEditUser({
      ...user,
      // normalize phone to digits-only when opening modal
      phoneNumber: cleanPhone(user.phoneNumber || ""),
    });
    setInitialEditUser({
      ...user,
      phoneNumber: cleanPhone(user.phoneNumber || ""),
    });
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    if (isUpdating) return;
    setIsEditModalOpen(false);
    setErrors({});
    setEditUser(initialEditUser);
    setIsEditChanged(false);
  };

  // Handle input changes for edit form
  // Handle input changes for edit form
  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditUser((prev) => {
      const updated = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
      if (!updated.phoneNumber) updated.receiveWhatsAppMessages = false;
      if (!updated.email) updated.receiveEmailMessages = false;
      setIsEditChanged(
        updated.firstName !== initialEditUser.firstName ||
          updated.lastName !== initialEditUser.lastName ||
          updated.email !== initialEditUser.email ||
          cleanPhone(updated.phoneNumber) !==
            cleanPhone(initialEditUser.phoneNumber) ||
          updated.receiveWhatsAppMessages !==
            initialEditUser.receiveWhatsAppMessages ||
          updated.receiveEmailMessages !== initialEditUser.receiveEmailMessages
      );
      return updated;
    });
  };

  // Handle Edit User
  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!validateEditForm()) return;

    setIsUpdating(true);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/user/update/${user._id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: editUser.firstName,
            lastName: editUser.lastName,
            email: editUser.email,
            phoneNumber: cleanPhone(editUser.phoneNumber),
            receiveWhatsAppMessages: editUser.receiveWhatsAppMessages,
            receiveEmailMessages: editUser.receiveEmailMessages,
          }),
        }
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update user");
      }

      const updatedUser = await response.json();
      setUser(updatedUser);
      setIsEditModalOpen(false);
      setEditUser({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        receiveWhatsAppMessages: false,
        receiveEmailMessages: false,
      });
      setInitialEditUser({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        receiveWhatsAppMessages: false,
        receiveEmailMessages: false,
      });
      setIsEditChanged(false);
      swalSuccess("Profile updated successfully");
    } catch (err) {
      swalError(err.message || "Failed to update profile.");
    } finally {
      setIsUpdating(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5 },
    },
  };

  let breadcrumbs = [
    { id: "home", name: "Home", path: "/admin" },
    {
      id: "user-profile",
      name: "User Profile",
      path: "/user-profile",
      isLast: true,
    },
  ];

  if (!user) {
    return (
      <>
        <Navbar breadcrumbs={breadcrumbs} isProfileHidden={true} />
        <motion.div
          className="user-profile-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className="user-profile-card error-card"
            variants={cardVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="user-profile-error">
              <motion.div
                className="user-profile-error-icon"
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              >
                ⚠️
              </motion.div>
              <h2 className="user-profile-error-title">
                Failed to Load Profile
              </h2>
              <p className="user-profile-error-message">
                Unable to retrieve user data. Please try refreshing the page or
                contact support if the problem persists.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </>
    );
  }

  return (
    <>
      <Navbar breadcrumbs={breadcrumbs} isProfileHidden={true} />
      <motion.div
        className="user-profile-container"
        variants={containerVariants}
        initial="hidden"
        animate={isLoaded ? "visible" : "hidden"}
      >
        <motion.div className="user-profile-card" variants={cardVariants}>
          {/* Header */}
          <motion.div className="user-profile-header" variants={itemVariants}>
            <motion.div
              className="user-profile-edit-icon"
              onClick={() => handleEditModalOpen(user)}
              whileHover={{ scale: 1.1, rotate: 15 }}
              whileTap={{ scale: 0.95 }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </motion.div>

            <motion.div
              className="user-profile-avatar"
              whileHover={{ scale: 1.05 }}
              animate={{
                boxShadow: [
                  "0 0 0 0 rgba(240, 154, 33, 0.4)",
                  "0 0 0 20px rgba(240, 154, 33, 0)",
                ],
              }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            >
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <path
                  d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle
                  cx="12"
                  cy="7"
                  r="4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </motion.div>

            <motion.h1
              className="user-profile-name"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              {user.firstName} {user.lastName}
            </motion.h1>
          </motion.div>

          {/* Profile Information */}
          <motion.div className="user-profile-info" variants={itemVariants}>
            {/* Email */}
            <motion.div
              className="user-profile-info-item"
              whileHover={{ x: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="user-profile-info-icon email-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="m4 4 16 0c1.1 0 2 .9 2 2l0 12c0 1.1-.9 2-2 2l-16 0c-1.1 0-2-.9-2-2l0-12c0-1.1 .9-2 2-2z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="m22 6-10 7L2 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="user-profile-info-content">
                <div className="user-profile-info-label">Email Address</div>
                <div className="user-profile-info-value">{user.email}</div>
              </div>
            </motion.div>

            {/* Phone */}
            <motion.div
              className="user-profile-info-item"
              whileHover={{ x: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="user-profile-info-icon phone-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="user-profile-info-content">
                <div className="user-profile-info-label">Phone Number</div>
                <div className="user-profile-info-value">
                  {user.phoneNumber || "Not provided"}
                </div>
              </div>
            </motion.div>

            {/* Email Messages */}
            <motion.div
              className="user-profile-info-item"
              whileHover={{ x: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="user-profile-info-icon notification-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="m13.73 21 a2 2 0 0 1-3.46 0"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="user-profile-info-content">
                <div className="user-profile-info-label">
                  Email Notifications
                </div>
                <div className="user-profile-status-indicator">
                  <motion.div
                    className={`user-profile-status-dot ${
                      user.receiveEmailMessages ? "enabled" : "disabled"
                    }`}
                    animate={
                      user.receiveEmailMessages ? { scale: [1, 1.2, 1] } : {}
                    }
                    transition={{ duration: 1, repeat: Infinity }}
                  ></motion.div>
                  <div className="user-profile-info-value">
                    {user.receiveEmailMessages ? "Enabled" : "Disabled"}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* WhatsApp Messages */}
            <motion.div
              className="user-profile-info-item"
              whileHover={{ x: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="user-profile-info-icon whatsapp-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="user-profile-info-content">
                <div className="user-profile-info-label">
                  WhatsApp Notifications
                </div>
                <div className="user-profile-status-indicator">
                  <motion.div
                    className={`user-profile-status-dot ${
                      user.receiveWhatsAppMessages ? "enabled" : "disabled"
                    }`}
                    animate={
                      user.receiveWhatsAppMessages ? { scale: [1, 1.2, 1] } : {}
                    }
                    transition={{ duration: 2, repeat: Infinity }}
                  ></motion.div>
                  <div className="user-profile-info-value">
                    {user.receiveWhatsAppMessages ? "Enabled" : "Disabled"}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Edit User Modal */}
      <AnimatePresence>
        {isEditModalOpen && (
          <Modal isOpen={isEditModalOpen} onClose={handleEditModalClose}>
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <h2>Edit Profile</h2>
              <p className="modal-description">
                Update the details below to modify your information.
              </p>
              <form onSubmit={handleEditUser} className="user-form">
                <div className="form-group">
                  <label>
                    <i className="fa-solid fa-asterisk requiredIcon"></i> First
                    Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={editUser.firstName}
                    placeholder="Enter first name"
                    onChange={handleEditInputChange}
                    required
                    disabled={isUpdating}
                  />
                  {errors.firstName && (
                    <motion.span
                      className="error"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                    >
                      {errors.firstName}
                    </motion.span>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    <i className="fa-solid fa-asterisk requiredIcon"></i> Last
                    Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={editUser.lastName}
                    placeholder="Enter last name"
                    onChange={handleEditInputChange}
                    required
                    disabled={isUpdating}
                  />
                  {errors.lastName && (
                    <motion.span
                      className="error"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                    >
                      {errors.lastName}
                    </motion.span>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    <i className="fa-solid fa-asterisk requiredIcon"></i> Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={editUser.email}
                    placeholder="Enter email address"
                    onChange={handleEditInputChange}
                    required
                    disabled={isUpdating}
                  />
                  {errors.email && (
                    <motion.span
                      className="error"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                    >
                      {errors.email}
                    </motion.span>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    Phone Number{" "}
                    <span className="optional-text">(Optional)</span>
                  </label>
                  <PhoneInput
                    placeholder="Enter Phone Number"
                    country={"in"}
                    value={editUser.phoneNumber}
                    onChange={(value, data, event, formattedValue) => {
                      const cleaned = cleanPhone(formattedValue || value || "");
                      setEditUser((prev) => {
                        const updated = {
                          ...prev,
                          phoneNumber: cleaned,
                          receiveWhatsAppMessages: cleaned
                            ? prev.receiveWhatsAppMessages
                            : false,
                        };
                        setIsEditChanged(
                          cleanPhone(updated.phoneNumber) !==
                            cleanPhone(initialEditUser.phoneNumber) ||
                            updated.firstName !== initialEditUser.firstName ||
                            updated.lastName !== initialEditUser.lastName ||
                            updated.email !== initialEditUser.email ||
                            updated.receiveWhatsAppMessages !==
                              initialEditUser.receiveWhatsAppMessages ||
                            updated.receiveEmailMessages !==
                              initialEditUser.receiveEmailMessages
                        );
                        return updated;
                      });
                    }}
                    inputStyle={{
                      height: "45px",
                      fontSize: "16px",
                      width: "100%",
                      paddingLeft: "48px",
                      border: "2px solid #e5efc5",
                      borderRadius: "8px",
                    }}
                    buttonStyle={{
                      height: "45px",
                      border: "2px solid #e5efc5",
                      borderRadius: "8px 0 0 8px",
                    }}
                    containerStyle={{
                      width: "100%",
                    }}
                    enableSearch={true}
                    disableSearchIcon={true}
                    dropdownClass="phone-input-dropdown"
                    dropdownStyle={{
                      maxHeight: "200px",
                      overflowX: "hidden",
                    }}
                    disabled={isUpdating}
                  />
                </div>

                <div className="form-group">
                  <label>Email Notifications</label>
                  <label className="solution-toggle">
                    <input
                      type="checkbox"
                      name="receiveEmailMessages"
                      checked={editUser.receiveEmailMessages}
                      onChange={handleEditInputChange}
                      disabled={isUpdating || !editUser.email}
                    />
                    <span className="toggle-track">
                      <span className="toggle-indicator"></span>
                    </span>
                    <span className="toggle-label-text">
                      {editUser.receiveEmailMessages ? "Enabled" : "Disabled"}
                    </span>
                  </label>
                </div>

                <div className="form-group">
                  <label>WhatsApp Notifications</label>
                  <label className="solution-toggle">
                    <input
                      type="checkbox"
                      name="receiveWhatsAppMessages"
                      checked={editUser.receiveWhatsAppMessages}
                      onChange={handleEditInputChange}
                      disabled={isUpdating || !editUser.phoneNumber}
                    />
                    <span className="toggle-track">
                      <span className="toggle-indicator"></span>
                    </span>
                    <span className="toggle-label-text">
                      {editUser.receiveWhatsAppMessages
                        ? "Enabled"
                        : "Disabled"}
                    </span>
                  </label>
                </div>

                {isUpdating ? (
                  <Loader />
                ) : (
                  <motion.button
                    type="submit"
                    className="btn-submit"
                    disabled={!isEditChanged || isUpdating}
                    whileHover={isEditChanged ? { scale: 1.02 } : {}}
                    whileTap={isEditChanged ? { scale: 0.98 } : {}}
                    onClick={(e) => {
                      if (!isEditChanged && !isUpdating) {
                        e.preventDefault();
                        swalWarning("No changes detected.");
                      }
                    }}
                  >
                    Update Profile
                  </motion.button>
                )}
              </form>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>
    </>
  );
};

export default UserProfile;
